import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { DecryptionData, ErrorTypes, IFormData, MessageActions, ValidFieldTypes } from '../../types';
import { ECardType, EPaymentMethod, prepareData, updateFrameHeight } from '../../utils';
import { DataProvider } from '../../context/dataContainer';
import { FormContainerProvider } from '../../context/formContainerContext';
import { getViewFormComponent } from '../../utils/view';
import { useHandleBroadcastChannel } from '../../hooks/useHandleBroadcastChannel';
import { bCService } from '../../events/broadcastChannel';
import { FieldViewProps } from '../types';


export const OtherFieldView = ({ data, setData, potentialCardType, setPotentialCardType }: FieldViewProps) => {
  const [search] = useSearchParams();
  const fieldType = search.get('field') ?? '';
  const clientId = search.get('clientId') ?? '';
  const sessionId = search.get('session') ?? '';
  const payPage = search.get('paypage') ?? '';
  const libInitializeId = search.get('libInitializeId') ?? '';

  const [isInit, setIsInit] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const methods = useForm<IFormData>({
    mode: 'onChange',
  });

  const {
    formState: { isSubmitting, errors },
    getFieldState,
    getValues,
    trigger,
  } = methods;

  const handleCardTypeChange = (potentialCardType: ECardType | null) => {
    bCService.broadcastChannelServicePost({
      action: MessageActions.CARD_TYPE_CHANGE,
      field: fieldType,
      potentialCardType,
    });
    sendCardTypeToLib(potentialCardType);
  };

  const updateHeight = () => updateFrameHeight(fieldType, sessionId, libInitializeId);

  const changePotentialCardType = (bcData) => {
    if (fieldType !== ValidFieldTypes.CVV && fieldType !== ValidFieldTypes.CARD_FEE) return;

    const newPotentialCardType = bcData.potentialCardType;
    setPotentialCardType(newPotentialCardType);
  };

  const getFieldsValues = async (event: MessageEvent) => {
    await trigger(fieldType as keyof IFormData);
    const fieldState = getFieldState(fieldType as keyof IFormData);
    const values = prepareData(getValues());
    setIsFetching(!fieldState.invalid);

    bCService.broadcastChannelServicePost({
      action: MessageActions.FIELD_VALUE,
      field: fieldType,
      value: values?.[fieldType] ?? '',
      fields: event.data.fields || [],
      error: (fieldState.invalid && fieldState.error) ? { 
        errorType: ErrorTypes.INVALID_FIELD,
        fieldType,
        errorText: fieldState.error.message,
      } : undefined,
      isError: fieldState.invalid,
    });
  };

  const handleMessage = (event: MessageEvent) => {
    if (event.data.action === MessageActions.GET_VALUE) {
      getFieldsValues(event);
    }
    if (event.data.action === MessageActions.ENABLE_FIELDS) {
      const fields = event.data.fields as string[] | undefined;
      if (!fields?.length || fields.includes(fieldType)) {
        setIsFetching(false);
      }
    }
  };

  const getPermissions = () => {
    bCService.broadcastChannelServicePost({
      action: MessageActions.PERMISSIONS,
      field: fieldType,
    });
  };

  const changePermissions = (bcData) => {
    if (isInit || bcData.field !== fieldType) return;

    setData(bcData.data);
    setPotentialCardType(bcData.potentialCardType);
    setIsInit(true);
  };

  const sendApplePayTokenToLib = (decryptionData: DecryptionData) => {
    window.parent.postMessage({
      action: MessageActions.APPLE_PAY_TOKEN,
      data: decryptionData,
      sessionId,
      libInitializeId,
    }, '*');
  };

  const sendGooglePayTokenToLib = (decryptionData: DecryptionData) => {
    window.parent.postMessage({
      action: MessageActions.GOOGLE_PAY_TOKEN,
      data: decryptionData,
      sessionId,
      libInitializeId,
    }, '*');
  };

  const sendCardTypeToLib = (potentialCardType: ECardType | null) => {
    window.parent.postMessage({
      action: MessageActions.CARD_TYPE_CHANGE,
      data: potentialCardType,
      sessionId,
      libInitializeId,
    }, '*');
  };

  const openTuaPayFrame = (url?: string, error?: unknown) => {
    window.parent.postMessage({
      action: MessageActions.TUAPAY_OPEN_FRAME,
      sessionId,
      libInitializeId,
      data: {
        ...(url !== undefined && { launchUrl: url }),
        ...(error !== undefined && { error }),
      },
    }, '*');
  };

  const notifyTuaPayBtnClicked = () => {
    window.parent.postMessage({
      action: MessageActions.TUAPAY_BTN_CLICKED,
      sessionId,
      libInitializeId,
    }, '*');
  };

  const notifyGooglePayBtnClicked = () => {
    window.parent.postMessage({
      action: MessageActions.GOOGLE_PAY_BTN_CLICKED,
      sessionId,
      libInitializeId,
    }, '*');
  };

  const notifyApplePayBtnClicked = () => {
    window.parent.postMessage({
      action: MessageActions.APPLE_PAY_BTN_CLICKED,
      sessionId,
      libInitializeId,
    }, '*');
  };

  useHandleBroadcastChannel({
    handleChangePotentialCardType: changePotentialCardType,
    handlePermissions: changePermissions,
  }, sessionId, libInitializeId);

  useEffect(() => {
    updateHeight();
  }, [isInit, errors[fieldType]]);

  useEffect(() => {
    getPermissions();

    window.addEventListener('message', handleMessage);
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  return (
    <>
      {!!isInit &&
        <DataProvider {...{
          data,
          clientId,
          session: sessionId,
          payPage,
          libInitializeId,
        }}>
          <FormContainerProvider
            params={{
              isFormLoading: isFetching,
              isFormSubmitted: isSubmitting,
            }}
          >
            <FormProvider { ...methods }>
              {getViewFormComponent(fieldType, {
                isFetching,
                handleCardTypeChange,
                potentialCardType,
                paymentType: fieldType === ValidFieldTypes.ACH_FEE
                  ? EPaymentMethod.ACH_ELECTRONIC_CHECK
                  : EPaymentMethod.PAY_WITH_CREDIT_CARD,
                toggleFetching: setIsFetching,
                sendApplePayTokenToLib,
                sendGooglePayTokenToLib,
                openTuaPayFrame,
                notifyTuaPayBtnClicked,
                notifyGooglePayBtnClicked,
                notifyApplePayBtnClicked,
                updateHeight,
              })}
            </FormProvider>
          </FormContainerProvider>
        </DataProvider>
      }
    </>
  );
};
